import React, { useState } from "react";
import authService from "../services/auth";
import "./Login.css";

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Login with email
      await authService.login({
        username: formData.email, // OAuth2 form expects 'username' field
        password: formData.password,
      });
      onLoginSuccess();
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="login-form-container">
          <div className="login-header">
            <img src="/anna-logo.png" alt="ANNA" className="login-logo" />
            <h1>Welcome to Anna!</h1>
            <p className="login-subtitle">
              Pop in your email and password to enter
              <br />
              the Anna Portal.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>

          <div className="login-footer">
            <a href="#privacy" className="footer-link">
              Privacy Policy
            </a>
            <a href="#terms" className="footer-link">
              Terms of Use
            </a>
          </div>
        </div>
      </div>

      <div className="login-right">
        <h2 className="right-title">
          A Modern Approach to
          <br />
          Autism Care
        </h2>

        <div className="feature-list">
          <div className="feature-item">
            <div className="feature-icon">📈</div>
            <div className="feature-content">
              <h3>Gold-standard services grounded in NDBI</h3>
              <p>
                We provide compassionate, personalized care using NDBI – the
                most proven approach for young children with autism.
              </p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon">🎯</div>
            <div className="feature-content">
              <h3>Naturalistic Services That Fit Into Everyday Life</h3>
              <p>
                Play-based, relationship-driven therapy that builds real-life
                skills through daily routines, play, and joyful connection.
              </p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon">❤️</div>
            <div className="feature-content">
              <h3>Neuroaffirming at Every Step</h3>
              <p>
                Respectful, strengths-based care that celebrates neurodiversity
                and empowers every child and family.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
